/* eslint-disable max-lines */
import moment from 'moment';
import {
  PM_PROVIDERS,
  DATE_FORMAT,
  validationStatusToMessageMap,
  PROVIDERS_MAP,
  SHORTCUT_ENTITIES_TYPES
} from 'common/constants/constants';
import jiraLogo from 'images/logos/jira-logo.svg';
import shortcutLogo from 'images/logos/shortcut-logo.svg';
import { Flex, Padding } from 'components/base/layout-components';
import Text from 'components/base/Text';
import Checkbox from 'components/base/Checkbox';
import { SPACES } from 'common/constants/sizes';
import AppRouter from 'common/AppRouter';
import { API } from 'common/constants/api-urls';
import axios from 'common/requester';
import {
  TIME_UNITS,
  convertMinutesToDisplayableTime
} from 'common/utils/time.utils';
import _ from 'lodash';
import { SENSOR_STATES } from '@linearb/linjs-common';
import { FILTER_TYPES } from 'common/constants/filters';
import { ITERATIONS_TYPES } from 'state/selectors/iterations-picker';
import { SENSOR_AUTH_TYPES } from 'common/services/sensors.service';

const AZURE_BOARDS_BASE_URL = 'https://dev.azure.com';

const QUERY_AUTO_SELECTION_JOB_SUITE_THRESHOLD_IN_MINS = 2;

const PROJECT_MANAGEMENT_PATH = AppRouter.PROJECT_MANAGEMENT.path;

export const managementToolView = location => {
  if (location === PROJECT_MANAGEMENT_PATH) {
    return false;
  } else {
    return location.split('/').pop() || location.split('/').pop();
  }
};

export const getSensorAuthorization = ({ sensorAuthorizations, sensorId }) =>
  getSensorBySensorId(sensorId, sensorAuthorizations);

export const CARDS = (history, isOrgSettings) => [
  {
    logo: jiraLogo,
    sensorId: PM_PROVIDERS.JIRA,
    name: 'Jira',
    onClick: () =>
      history.push(
        `${PROJECT_MANAGEMENT_PATH}/jira${
          isOrgSettings ? '?isOrgSettings=true' : ''
        }`
      )
  },
  {
    logo: shortcutLogo,
    sensorId: PM_PROVIDERS.CLUBHOUSE,
    name: 'Shortcut',
    onClick: () =>
      history.push(
        `${PROJECT_MANAGEMENT_PATH}/clubhouse${
          isOrgSettings ? '?isOrgSettings=true' : ''
        }`
      )
  },
  {
    logo: PROVIDERS_MAP.azure_boards.logo,
    sensorId: PROVIDERS_MAP.azure_boards.sensor_id,
    name: PROVIDERS_MAP.azure_boards.name,
    onClick: () =>
      history.push(
        `${PROJECT_MANAGEMENT_PATH}/${
          PROVIDERS_MAP.azure_boards.provider_path
        }${isOrgSettings ? '?isOrgSettings=true' : ''}`
      )
  }
];

export const getIterationsTableColumns = [
  {
    name: 'ITERATION',
    dataPoint: 'name',
    ColumnHeaderComponent: () => (
      <Padding l='x-big'>
        <Text color='gray' letterSpacing='0.01071em'>
          ITERATION
        </Text>
      </Padding>
    )
  },
  {
    name: 'BOARD',
    dataPoint: 'board'
  },
  {
    name: 'START DATE',
    dataPoint: 'start_date'
  },
  {
    name: 'END DATE',
    dataPoint: 'end_date'
  },
  {
    name: 'DURATION',
    dataPoint: 'duration'
  }
];

export const getIterationsSkeletonColumns = [
  'singleline-random',
  'singleline-random',
  'singleline-random',
  'singleline-random'
];

export const getFilteredterationsTableData = (
  {
    id,
    name,
    start_date,
    end_date,
    isSelected,
    provider_attributes: providerAttributes,
    board_id: boardId,
    type
  },
  onIterationSelection,
  providerBoards,
  dbBoardsMap
) => {
  const originProviderId = _.get(providerAttributes, 'origin_board_id');
  const originBoardName =
    originProviderId && _.get(providerBoards, `${originProviderId}.name`);
  const currentBoardName = _.get(dbBoardsMap, `${boardId}.name`);

  const durationInMinutes =
    start_date &&
    end_date &&
    moment(end_date).diff(moment(start_date), 'minutes');

  const duration =
    durationInMinutes && durationInMinutes > 0
      ? convertMinutesToDisplayableTime({
          minutes: durationInMinutes,
          maxTimeUnit: TIME_UNITS.days
        })
      : { value: '-', label: '' };
  // in case of kanban we want end date to be inclusive
  const kanbanDiff = type === ITERATIONS_TYPES.KANBAN ? 1 : 0;

  return {
    name: (
      <Flex items='center'>
        <Checkbox
          checked={isSelected}
          onChange={e => onIterationSelection(id)}
        />
        <Text size='medium' pl={SPACES.small} data-cy='iteration-name'>
          {name}
        </Text>
      </Flex>
    ),
    board: (
      <Text size='medium' data-cy='iteration-start-date'>
        {originBoardName || currentBoardName || ''}
      </Text>
    ),
    start_date: (
      <Text size='medium' data-cy='iteration-start-date'>
        {moment(start_date).format(DATE_FORMAT)}
      </Text>
    ),
    end_date: <Text size='medium'>{moment(end_date).format(DATE_FORMAT)}</Text>,
    duration: (
      <Text size='medium'>
        {duration.value + kanbanDiff} {duration.label}
      </Text>
    )
  };
};

export const validateCHToken = async token => {
  const url = `${API.TEST_API_TOKEN_PATH}/clubhouse`;
  const body = {
    access_token: token
  };

  try {
    const { token_status, username } = await axios.post(url, body);
    if (token_status === validationStatusToMessageMap.VALID) {
      return Promise.resolve({ token_status, username });
    }
    return Promise.reject(token_status);
  } catch (e) {
    return Promise.reject(e);
  }
};

export const validateAzureBoardsToken = async (token, orgName) => {
  const url = `${API.TEST_API_TOKEN_PATH}/${PROVIDERS_MAP.azure_boards.provider_path}`;
  const body = {
    access_token: token,
    base_url: `${AZURE_BOARDS_BASE_URL}/${orgName}`
  };

  try {
    const { token_status, username } = await axios.post(url, body);
    if (token_status === validationStatusToMessageMap.VALID) {
      return Promise.resolve({ token_status, username });
    }
    return Promise.reject(token_status);
  } catch (e) {
    return Promise.reject(e);
  }
};

export const extractOrgNameFromAzureBoardsSensor = azureBoardsSensor => {
  const baseUrl = _.get(azureBoardsSensor, 'profile.base_url', '');
  const baseUrlArr = baseUrl.split('/');
  return _.last(baseUrlArr);
};

export const getAzureBoardsNewSensorBody = ({ token, orgName, username }) => ({
  sensor_id: PROVIDERS_MAP.azure_boards.sensor_id,
  state: SENSOR_STATES.ok,
  token: {
    token,
    refresh_token: null,
    is_proxied: false
  },
  profile: {
    base_url: `${AZURE_BOARDS_BASE_URL}/${orgName}`,
    version: 'cloud',
    username,
    orgName,
    auth_type: SENSOR_AUTH_TYPES.TOKEN
  },
  name: PROVIDERS_MAP.azure_boards.name
});

export const getAzureBoardsOrCHReauthSensorBody = ({ newToken }) => ({
  state: SENSOR_STATES.ok,
  failing_reason: null,
  token: {
    token: newToken,
    refresh_token: null,
    is_proxied: false
  }
});

export const createCHSensorAuth = async ({
  token,
  username,
  createSensorAuthorization,
  isTeamLicense
}) => {
  const body = {
    sensor_id: PM_PROVIDERS.CLUBHOUSE,
    state: 'ok',
    token: {
      token,
      refresh_token: null,
      is_proxied: false
    },
    profile: {
      username,
      auth_type: SENSOR_AUTH_TYPES.TOKEN
    },
    name: 'ClubHouse'
  };

  return createSensorAuthorization(
    body,
    !isTeamLicense ? { withPostActions: true } : {}
  );
};

const getSensorBySensorId = (id, sensorAuthorizations) =>
  sensorAuthorizations.find(({ sensor_id }) => sensor_id === id);

export const normalizeProviderEntitiesData = list =>
  _.get(list, 'entities', []).map(
    ({
      entity_id,
      entity_name,
      data: {
        description,
        project_type,
        avatar_urls = {},
        provider_key,
        stats: { num_stories = '', story_points = '' } = {}
      }
    }) => ({
      provider_id: String(entity_id),
      name: entity_name,
      project_type,
      description,
      num_stories,
      story_points,
      avatar_urls,
      provider_key
    })
  );

export const normalizePMEntitiesDataFromDDB = list => {
  return (list?.items || []).map(
    ({
      value,
      entity_id,
      dedupe_key,
      extra: {
        project_type,
        avatar_urls,
        num_issues,
        provider_key,
        description,
        updated_at
      }
    }) => {
      return {
        provider_id: entity_id ? String(entity_id) : value,
        name: value,
        project_type,
        provider_key,
        num_stories: num_issues,
        avatar_urls,
        description,
        num_issues,
        updated_at
      };
    }
  );
};

export const formatEntitiesToPmImportSettingPayload = (
  entities,
  keys = ['provider_id']
) => entities.map(entity => _.pick(entity, keys));

const formatCustomFieldsToPmImportSettingPayload = customFields =>
  customFields.map(({ id, value }) => ({
    id,
    value
  }));

export const shouldStartAutoSelectionPooling = sensorAuthorization => {
  const createdAt = moment(sensorAuthorization.created_at).utc();
  const now = moment().utc();
  const duration = moment.duration(now.diff(createdAt));
  const minutes = duration.asMinutes();
  return minutes < QUERY_AUTO_SELECTION_JOB_SUITE_THRESHOLD_IN_MINS;
};

export const generatePmImportPayloadFromEntitiesUnderFF = {
  [PM_PROVIDERS.JIRA]: entities => {
    return {
      boards: formatEntitiesToPmImportSettingPayload(
        _.get(entities, 'boards', []),
        ['provider_id', 'name']
      ),
      labels: formatEntitiesToPmImportSettingPayload(
        _.get(entities, 'labels', []),
        ['name']
      ),
      fixVersions: formatEntitiesToPmImportSettingPayload(
        _.get(entities, 'fixVersions', []),
        ['name']
      ),
      epics: formatEntitiesToPmImportSettingPayload(
        _.get(entities, 'epics', []),
        ['provider_id', 'provider_key']
      ),
      fields: formatCustomFieldsToPmImportSettingPayload(
        _.get(entities, 'customFields', [])
      )
    };
  },
  [PM_PROVIDERS.CLUBHOUSE]: entities => {
    return {
      [PROVIDERS_MAP.clubhouse.entities_settings_keys.projects]:
        formatEntitiesToPmImportSettingPayload(
          _.get(entities, FILTER_TYPES.PROJECTS, []),
          ['provider_id', 'name']
        ),
      [PROVIDERS_MAP.clubhouse.entities_settings_keys.teams]:
        formatEntitiesToPmImportSettingPayload(
          _.get(entities, FILTER_TYPES.TEAMS, []),
          ['provider_id', 'name']
        ),
      [PROVIDERS_MAP.clubhouse.entities_settings_keys.labels]:
        formatEntitiesToPmImportSettingPayload(
          _.get(entities, FILTER_TYPES.LABEL, []),
          ['name']
        ),
      [PROVIDERS_MAP.clubhouse.entities_settings_keys.epics]:
        formatEntitiesToPmImportSettingPayload(
          _.get(entities, FILTER_TYPES.EPIC, [])
        )
    };
  }
};

export const generatePmImportPayloadFromEntities = {
  [PM_PROVIDERS.JIRA]: (
    entities,
    entityName = PROVIDERS_MAP.jira.entity_name
  ) => {
    return {
      [PROVIDERS_MAP.jira.entities_settings_keys[entityName]]:
        formatEntitiesToPmImportSettingPayload(entities, [
          'provider_id',
          'name'
        ])
    };
  },
  [PM_PROVIDERS.CLUBHOUSE]: entities => {
    const teams = entities.filter(
      ({ entityType }) => entityType === SHORTCUT_ENTITIES_TYPES.TEAM
    );
    const projects = entities.filter(
      ({ entityType }) => entityType === SHORTCUT_ENTITIES_TYPES.PROJECT
    );
    return {
      [PROVIDERS_MAP.clubhouse.entities_settings_keys.projects]:
        formatEntitiesToPmImportSettingPayload(projects),
      [PROVIDERS_MAP.clubhouse.entities_settings_keys.teams]:
        formatEntitiesToPmImportSettingPayload(teams, ['provider_id', 'name'])
    };
  },
  [PM_PROVIDERS.AZURE_BOARDS]: entities => {
    return {
      [PROVIDERS_MAP.azure_boards.entities_settings_keys.projects]:
        formatEntitiesToPmImportSettingPayload(entities, [
          'provider_id',
          'name'
        ])
    };
  }
};

export const getTooManyEntitiesSelectedErrorMessage = ({
  entityName,
  limit,
  deviation
}) =>
  `The organization-wide ${entityName}s limit of ${limit} has been reached. Please deselect some boards to stay within the limit.`;
